import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { KPIGroup, NumberValue, Widget } from '@heroui-pro/react';
import { KPI } from '@heroui-pro/react/kpi';
import type { RevenueSummary } from '@/api/models';
import {
    REVENUE_SUMMARY_PERIOD_ICON,
    REVENUE_SUMMARY_PERIOD_LABEL_KEY,
    getPercentageTrend,
    showsTrend,
    toFractionalPercent,
} from './periodMeta';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { formatGel } from '@/utils/money';

type RevenueSummaryViewProps = {
    data: RevenueSummary;
};

export const RevenueSummaryView: React.FC<RevenueSummaryViewProps> = ({
    data,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.revenueSummary.title'),
        }),
        [t],
    );

    return (
        <section
            aria-labelledby="revenue-summary-widget-title"
            {...getNodeTestIdProp('revenue-summary-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('revenue-summary-widget')}
            >
                <Widget.Header
                    {...getNodeTestIdProp('revenue-summary-widget-header')}
                >
                    <Widget.Title
                        {...getNodeTestIdProp('revenue-summary-widget-title')}
                    >
                        {copy.title}
                    </Widget.Title>
                </Widget.Header>
                <Widget.Content
                    {...getNodeTestIdProp('revenue-summary-widget-content')}
                >
                    <KPIGroup className="bg-transparent shadow-none flex-col sm:flex-row">
                        {data.items.map((item, index) => {
                            const kpiId = `revenue-summary-item-${item.period}`;
                            const trend = getPercentageTrend(
                                item.changePercentage,
                            );
                            return (
                                <React.Fragment key={item.period}>
                                    <KPI
                                        {...getNodeTestIdProp(
                                            `revenue-summary-item-${item.period}`,
                                        )}
                                        id={kpiId}
                                    >
                                        <KPI.Header
                                            {...getNodeTestIdProp(
                                                `revenue-summary-item-header-${item.period}`,
                                            )}
                                        >
                                            <KPI.Icon
                                                {...getNodeTestIdProp(
                                                    `revenue-summary-item-icon-wrap-${item.period}`,
                                                )}
                                            >
                                                <Icon
                                                    className="size-4"
                                                    {...getNodeTestIdProp(
                                                        `revenue-summary-item-icon-${item.period}`,
                                                    )}
                                                    icon={
                                                        REVENUE_SUMMARY_PERIOD_ICON[
                                                            item.period
                                                        ]
                                                    }
                                                />
                                            </KPI.Icon>
                                            <KPI.Title
                                                {...getNodeTestIdProp(
                                                    `revenue-summary-item-title-${item.period}`,
                                                )}
                                            >
                                                {t(
                                                    REVENUE_SUMMARY_PERIOD_LABEL_KEY[
                                                        item.period
                                                    ],
                                                )}
                                            </KPI.Title>
                                            {trend !== null &&
                                            showsTrend(item) ? (
                                                <KPI.Trend
                                                    className="ms-auto"
                                                    {...getNodeTestIdProp(
                                                        `revenue-summary-item-trend-${item.period}`,
                                                    )}
                                                    trend={trend}
                                                    variant="tertiary"
                                                >
                                                    <NumberValue
                                                        maximumFractionDigits={
                                                            2
                                                        }
                                                        minimumFractionDigits={
                                                            0
                                                        }
                                                        signDisplay="exceptZero"
                                                        style="percent"
                                                        value={toFractionalPercent(
                                                            item.changePercentage,
                                                        )}
                                                        {...getNodeTestIdProp(
                                                            `revenue-summary-item-trend-value-${item.period}`,
                                                        )}
                                                    />
                                                </KPI.Trend>
                                            ) : null}
                                        </KPI.Header>
                                        <KPI.Content
                                            {...getNodeTestIdProp(
                                                `revenue-summary-item-content-${item.period}`,
                                            )}
                                        >
                                            {/* `formatGel` on the tetri twin,
                                                not Intl's currency style: the
                                                latter renders `GEL 455.00`
                                                while every other money figure
                                                in the app renders `₾ 455.00`
                                                (BOS-171). `value` is still
                                                passed so KPI keeps its own
                                                semantics. */}
                                            <KPI.Value
                                                {...getNodeTestIdProp(
                                                    `revenue-summary-item-value-${item.period}`,
                                                )}
                                                id={`${kpiId}-value`}
                                                value={item.amount}
                                            >
                                                {() =>
                                                    formatGel(item.amountTetri)
                                                }
                                            </KPI.Value>
                                        </KPI.Content>
                                    </KPI>
                                    {index < data.items.length - 1 ? (
                                        <KPIGroup.Separator
                                            className="h-px w-full sm:h-auto sm:w-px"
                                            {...getNodeTestIdProp(
                                                `revenue-summary-item-separator-${item.period}`,
                                            )}
                                        />
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </KPIGroup>
                </Widget.Content>
            </Widget>
        </section>
    );
};
